/**
 * ApprovalWorkflow-Help — inline contextual help for the approval-workflow UI.
 *
 * Renders a small (i) icon (via the `apw-help` span written directly in the view templates) that, when
 * clicked, pops an anchored explanation of what a control/section does and how to use it. A single popover
 * element is managed on document.body so it works from any view (editor, list, bindings) and dismisses on
 * outside-click / Escape.
 *
 * Inline template handlers reach this through the `window.ApprovalWorkflowHelp` global (set on load) —
 * unusual for a pict module, but this is a cross-view UI utility, not a view, so routing through a single
 * `_Pict.views.X` would be arbitrary. Templates call: ApprovalWorkflowHelp.show('<topicId>', this).
 */

// Topic content. Body is trusted HTML (authored here, never user input).
const HELP =
{
	'list.overview':
	{
		Title: 'Approval workflows',
		Body: '<p>An <b>approval workflow</b> governs how a document (or observation) moves between statuses — for example <i>Draft → Submitted → Approved</i>.</p>'
			+ '<p>Each workflow is a set of <b>states</b> (statuses) connected by <b>transitions</b> (the actions people take to move between them). Open one to see it as a diagram, or bind workflows to document types with <b>Document bindings</b>.</p>',
	},
	'editor.graph':
	{
		Title: 'Reading the diagram',
		Body: '<p>Each <b>box</b> is a state; its coloured header shows the status, and the body shows its display name. Each <b>arrow</b> is a transition — the label is the action, and the arrow is coloured to match the state it leads to.</p>'
			+ '<p><b>Click an arrow</b> to read who can perform it and what it does. <b>Drag the background</b> to pan and <b>scroll</b> to zoom; the toolbar has zoom and fit controls.</p>',
	},
	'editor.edit':
	{
		Title: 'Editing a workflow',
		Body: '<p><b>Edit</b> lets a customer admin change this workflow. Click a state or transition to edit it in the side panel, or use <b>Add state</b>.</p>'
			+ '<p>In a panel, <b>Apply to diagram</b> puts your edits onto the diagram so you can see them — but nothing is stored yet. Moving to another panel or pressing <b>Save</b> applies them for you.</p>'
			+ '<p>Changes are held until you press <b>Save</b> — <b>Discard</b> reverts everything to the last saved version. Saving writes back the shared configuration this and the field apps read. Settings this screen has no controls for, such as branches, triggers and actor tags, are kept as they are.</p>',
	},
	'transition.action':
	{
		Title: 'Action',
		Body: '<p>The verb a person performs to make this transition — for example <i>Submit</i>, <i>Approve</i>, or <i>Reject</i>. It appears as the label on the arrow.</p>',
	},
	'transition.target':
	{
		Title: 'Goes to state',
		Body: '<p>The state the entity moves into when this action is taken. Only existing states appear here — add the state first if you need a new destination.</p>',
	},
	'transition.flags':
	{
		Title: 'Transition options',
		Body: '<ul><li><b>Locks / Unlocks entity</b> — whether the document becomes locked (read-only) or unlocked after this action. An action can lock or unlock, not both.</li>'
			+ '<li><b>Is an approval</b> — marks this as a formal approval step (used for approval tracking and permissions).</li>'
			+ '<li><b>Allowed on locked</b> — lets this action run even while the entity is locked.</li></ul>',
	},
	'transition.actors':
	{
		Title: 'Who can perform this action',
		Body: '<p>The people allowed to take this transition. Add any mix of:</p>'
			+ '<ul><li><b>Roles</b> — anyone with a given role (optionally only project members, or the lab from the form).</li>'
			+ '<li><b>A specific user</b>.</li>'
			+ '<li><b>Special groups</b> — Administrators, the user who created the entity, higher-level users, or a minimum project level.</li></ul>'
			+ '<p>If no one is assigned, the action can’t be performed.</p>',
	},
	'state.name':
	{
		Title: 'Display name',
		Body: '<p>The human-readable name shown on the state box and in the generated sentences (e.g. “Waiting for Project Engineer Approval”).</p>',
	},
	'state.status':
	{
		Title: 'State hash (identity)',
		Body: '<p>The state’s internal identity — the value transitions reference as their destination. Renaming it automatically updates every transition that points at this state, so links aren’t broken.</p>',
	},
	'state.transitions':
	{
		Title: 'Transitions out of this state',
		Body: '<p>The actions that lead <b>out</b> of this state. Click one to edit its action, destination, options, and who can perform it, or use <b>Add transition</b> to create a new one.</p>',
	},
	'bindings.overview':
	{
		Title: 'Document bindings',
		Body: '<p>Bindings decide <b>which approval workflow governs each document type</b>. Pick a workflow for a document type and press <b>Save</b>.</p>'
			+ '<p>A document type with no binding of its own uses the <b>Default</b>.</p>'
			+ '<p>Bindings apply at two levels — the <b>customer default</b> that every project inherits, and per-<b>project</b> overrides. Use the <b>Scope</b> switch to choose which you are editing.</p>',
	},
	'bindings.scope':
	{
		Title: 'Binding scope',
		Body: '<p><b>Customer default</b> is the organization-wide set of bindings. Every project inherits it unless it sets its own.</p>'
			+ '<p><b>Specific project</b> lets one project <b>override</b> individual bindings — the rest still fall back to the customer default. Pick a project to view or edit its overrides; a project marked <i>customized</i> already has some.</p>'
			+ '<p>Saving a project writes only the rows that differ from the customer default, so the two stay in sync except where you deliberately diverge.</p>',
	},
	'bindings.dots':
	{
		Title: 'The status dots',
		Body: '<ul><li><b>Green dot</b> — this type <b>is governed</b> by an approval workflow (the one shown in the dropdown).</li>'
			+ '<li><b>Hollow dot</b> — this type is <b>not governed</b>; documents of this type skip approvals.</li>'
			+ '<li><b>Blue-ringed dot</b> (project scope only) — this project <b>overrides the customer default</b> for this type. Rows without the ring inherit the default.</li></ul>'
			+ '<p>Hover any dot for its exact meaning.</p>',
	},
	'bindings.default':
	{
		Title: 'Default binding',
		Body: '<p>The fallback workflow applied to any document type that doesn’t have its own binding set below.</p>',
	},
};

let _popover = null;

function _ensureCSS()
{
	if (typeof document === 'undefined' || document.getElementById('apw-help-css')) { return; }
	const tmpStyle = document.createElement('style');
	tmpStyle.id = 'apw-help-css';
	tmpStyle.textContent = /*css*/`
		.apw-help { display: inline-flex; align-items: center; justify-content: center; cursor: pointer; color: var(--bulma-text-weak, #9aa4b2);
			font-size: 0.82em; margin-left: 0.3em; vertical-align: middle; transition: color 0.12s; }
		.apw-help:hover { color: var(--bulma-link, #156dd1); }
		.apw-help-pop { position: fixed; z-index: 9999; background: var(--bulma-scheme-main, #fff); border: 1px solid var(--bulma-border, #d6dbe4);
			border-radius: 10px; box-shadow: 0 8px 30px rgba(20,40,80,0.20); overflow: hidden; font-size: 0.86rem; color: #2c3e50; }
		.apw-help-pop-head { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.5rem 0.75rem;
			background: var(--bulma-scheme-main-bis, #f4f6fa); border-bottom: 1px solid var(--bulma-border, #e6e9ef); font-weight: 700; }
		.apw-help-pop-close { border: none; background: transparent; cursor: pointer; font-size: 1.1rem; line-height: 1; color: var(--bulma-text-weak, #8a93a3); }
		.apw-help-pop-close:hover { color: #2c3e50; }
		.apw-help-pop-body { padding: 0.7rem 0.8rem; line-height: 1.5; }
		.apw-help-pop-body p { margin: 0 0 0.5rem; } .apw-help-pop-body p:last-child { margin-bottom: 0; }
		.apw-help-pop-body ul { margin: 0.25rem 0 0.5rem; padding-left: 1.1rem; } .apw-help-pop-body li { margin-bottom: 0.25rem; }
	`;
	document.head.appendChild(tmpStyle);
}

function _onDocDown(pEvent)
{
	if (_popover && !_popover.contains(pEvent.target)) { hide(); }
}

function _onKey(pEvent)
{
	if (pEvent.key === 'Escape') { hide(); }
}

/** Show the help popover for a topic, anchored under the clicked icon. */
function show(pTopicId, pAnchorEl)
{
	const tmpTopic = HELP[pTopicId];
	if (!tmpTopic || typeof document === 'undefined') { return; }
	hide();
	_ensureCSS();
	const tmpPop = document.createElement('div');
	tmpPop.className = 'apw-help-pop';
	tmpPop.innerHTML = `<div class="apw-help-pop-head"><span>${tmpTopic.Title}</span>`
		+ `<button class="apw-help-pop-close" aria-label="Close" onclick="ApprovalWorkflowHelp.hide()">&times;</button></div>`
		+ `<div class="apw-help-pop-body">${tmpTopic.Body}</div>`;
	document.body.appendChild(tmpPop);
	_popover = tmpPop;

	// Position under the icon, kept inside the viewport.
	const tmpWidth = 320;
	tmpPop.style.width = `${tmpWidth}px`;
	const tmpRect = pAnchorEl.getBoundingClientRect();
	let tmpLeft = tmpRect.left;
	if (tmpLeft + tmpWidth > window.innerWidth - 8) { tmpLeft = window.innerWidth - tmpWidth - 8; }
	if (tmpLeft < 8) { tmpLeft = 8; }
	let tmpTop = tmpRect.bottom + 6;
	// If it would run off the bottom, flip above the icon.
	if (tmpTop + tmpPop.offsetHeight > window.innerHeight - 8)
	{
		tmpTop = Math.max(8, tmpRect.top - tmpPop.offsetHeight - 6);
	}
	tmpPop.style.left = `${tmpLeft}px`;
	tmpPop.style.top = `${tmpTop}px`;

	// Dismiss on outside click / Escape (added on the next tick so this very click doesn't close it).
	setTimeout(() =>
	{
		document.addEventListener('mousedown', _onDocDown, true);
		document.addEventListener('keydown', _onKey, true);
	}, 0);
}

function hide()
{
	if (_popover)
	{
		_popover.remove();
		_popover = null;
		document.removeEventListener('mousedown', _onDocDown, true);
		document.removeEventListener('keydown', _onKey, true);
	}
}

/** The (i) icon markup for a topic (for use inside view templates). */
function icon(pTopicId)
{
	return `<span class="apw-help" role="button" tabindex="0" title="What's this?" onclick="ApprovalWorkflowHelp.show('${pTopicId}', this)"><i class="fas fa-circle-info"></i></span>`;
}

const _module = { HELP, show, hide, icon };

// Expose for inline template onclick handlers.
if (typeof window !== 'undefined')
{
	window.ApprovalWorkflowHelp = _module;
}

module.exports = _module;
